import { useState, useEffect, useMemo } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  type Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { Bevinding } from "@/features/gebreken/types/bevinding";

interface UseBevindigenOptions {
  includeDeleted?: boolean;
}

interface UseBevindigenResult {
  bevindingen: Bevinding[];
  activeBevindingen: Bevinding[];
  deletedBevindingen: Bevinding[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook to subscribe to bevindingen for a project in real-time
 * Returns bevindingen sorted by created_at descending (newest first)
 */
export function useBevindingen(
  userId: string | null,
  projectId: string | null,
  options: UseBevindigenOptions = {}
): UseBevindigenResult {
  const { includeDeleted = true } = options;
  const [bevindingen, setBevindingen] = useState<Bevinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state when IDs change
    setBevindingen([]);
    setError(null);

    if (!userId || !projectId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Create query for bevindingen subcollection
    const bevindigenRef = collection(
      db(),
      "users",
      userId,
      "projects",
      projectId,
      "bevindingen"
    );
    const q = query(bevindigenRef, orderBy("created_at", "desc"));

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const bevindigenData: Bevinding[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            gebrek_kort: data.gebrek_kort || "",
            gebrek: data.gebrek || "",
            maatregel: data.maatregel || "",
            voorschrift: data.voorschrift || [],
            afbeeldingen: data.afbeeldingen || [],
            deleted: data.deleted || false,
            created_at: data.created_at as Timestamp,
            updated_at: data.updated_at as Timestamp | undefined,
          };
        });
        setBevindingen(bevindigenData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error fetching bevindingen:", err);
        setError("Kon bevindingen niet laden");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, projectId]);

  // Memoized filtered lists
  const activeBevindingen = useMemo(
    () => bevindingen.filter((b) => !b.deleted),
    [bevindingen]
  );

  const deletedBevindingen = useMemo(
    () => bevindingen.filter((b) => b.deleted),
    [bevindingen]
  );

  // Return all or only active based on options
  const filteredBevindingen = includeDeleted ? bevindingen : activeBevindingen;

  return {
    bevindingen: filteredBevindingen,
    activeBevindingen,
    deletedBevindingen,
    loading,
    error,
  };
}
